/**
 * Meals — branch-admin CRUD on the canteen menu.
 *
 * Lists meal plans grouped by date, supports kind tags (breakfast / snack /
 * lunch / evening_snack / dinner), allergens, age groups, and archived
 * filter. Branch admins can add / edit / archive — everyone else (teachers,
 * parents) sees a read-only view scoped to their branch.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Panel, Pill, EmptyState, StatCard } from "@/components/Bits";
import {
  ForkKnife, Plus, PencilSimple, Archive, Eye, EyeSlash, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const MEAL_TYPES = ["breakfast", "snack", "lunch", "evening_snack", "dinner"];
const ALL_ALLERGENS = ["dairy", "nuts", "egg", "gluten", "soy", "seafood"];

export default function Meals() {
  const { user } = useAuth();
  const canManage = ["super_admin", "hq_admin", "branch_admin", "centre_head", "principal"].includes(user?.role);
  const [meals, setMeals] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/meals", { params: { include_archived: showArchived } });
      setMeals(data);
    } catch (e) {
      toast.error("Could not load meals");
    }
  };
  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line

  const archive = async (m) => {
    if (!confirm(`Archive ${m.meal_type} on ${m.plan_date}?`)) return;
    try {
      await api.post(`/meals/${m.meal_id}/archive`);
      toast.success("Archived"); await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const grouped = useMemo(() => {
    const by = {};
    // Skip legacy rows that don't fit the new schema (e.g. seed data with
    // the old `breakfast/lunch/snack` shape — those render via parent meals
    // page, not this admin grid).
    meals.filter((m) => m && m.meal_type && m.plan_date).forEach((m) => {
      if (!by[m.plan_date]) by[m.plan_date] = [];
      by[m.plan_date].push(m);
    });
    return Object.entries(by).sort((a, b) => (a[0] > b[0] ? -1 : 1));
  }, [meals]);

  return (
    <div data-testid="meals-page">
      <PageHeader eyebrow="Nutrition" title="Meal plans"
        subtitle="Branch-scoped menu — parents see it instantly."
        action={canManage && (
          <button onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover"
            data-testid="new-meal-btn">
            <Plus weight="bold" /> New meal
          </button>
        )} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Active" value={meals.filter((m) => !m.archived).length} icon={ForkKnife} accent="terracotta" />
        <StatCard label="Distinct types" value={new Set(meals.map((m) => m.meal_type).filter(Boolean)).size} icon={Eye} accent="mustard" />
      </div>

      {canManage && (
        <div className="mb-4">
          <button onClick={() => setShowArchived((v) => !v)} data-testid="meals-toggle-archived"
            className="px-3 py-1.5 text-xs rounded-full bg-white border border-border inline-flex items-center gap-1.5">
            {showArchived ? <EyeSlash size={14} /> : <Eye size={14} />}
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
        </div>
      )}

      {grouped.length === 0 ? (
        <EmptyState title="No meal plans yet"
          hint={canManage ? "Add a meal to start the menu." : "Your branch admin hasn't published a menu yet."} />
      ) : grouped.map(([date, items]) => (
        <Panel key={date} className="mb-4" testid={`meals-date-${date}`}>
          <div className="font-heading text-lg font-bold mb-2">{date}</div>
          <div className="space-y-2">
            {items.map((m) => (
              <div key={m.meal_id}
                className={`p-3 rounded-2xl border border-border flex items-center justify-between gap-3 ${m.archived ? "opacity-60" : "bg-white"}`}
                data-testid={`meal-${m.meal_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <Pill tone="lavender">{m.meal_type.replace("_", " ")}</Pill>
                    {(m.age_groups || []).map((a) => <Pill key={a} tone="sage">{a}</Pill>)}
                    {m.archived && <Pill tone="terracotta">Archived</Pill>}
                  </div>
                  <div className="text-sm mt-1.5">{(m.items || []).join(" · ")}</div>
                  {(m.allergens || []).length > 0 && (
                    <div className="text-xs mt-1.5 text-terracotta-deep flex items-center gap-1.5">
                      <WarningCircle weight="duotone" /> Allergens: {m.allergens.join(", ")}
                    </div>
                  )}
                  {m.notes && <div className="text-xs text-ink-soft mt-1">{m.notes}</div>}
                </div>
                {canManage && (
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setEditing(m)} className="p-1.5 rounded-lg hover:bg-paper"
                      data-testid={`edit-meal-${m.meal_id}`}>
                      <PencilSimple size={14} weight="duotone" />
                    </button>
                    {!m.archived && (
                      <button onClick={() => archive(m)} className="p-1.5 rounded-lg hover:bg-paper text-red-600"
                        data-testid={`archive-meal-${m.meal_id}`}>
                        <Archive size={14} weight="duotone" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      ))}

      {(creating || editing) && (
        <MealDialog meal={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onDone={() => { setCreating(false); setEditing(null); load(); }} />
      )}
    </div>
  );
}

function MealDialog({ meal, onClose, onDone }) {
  const isEdit = !!meal;
  const [f, setF] = useState({
    plan_date: meal?.plan_date || new Date().toISOString().slice(0, 10),
    meal_type: meal?.meal_type || "lunch",
    items: (meal?.items || []).join(", "),
    age_groups: meal?.age_groups || [],
    allergens: meal?.allergens || [],
    notes: meal?.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const items = f.items.split(",").map((s) => s.trim()).filter(Boolean);
    if (items.length === 0) { toast.error("List at least one item."); return; }
    setBusy(true);
    try {
      const payload = {
        plan_date: f.plan_date, meal_type: f.meal_type,
        items, age_groups: f.age_groups, allergens: f.allergens, notes: f.notes,
      };
      if (isEdit) {
        await api.patch(`/meals/${meal.meal_id}`, payload);
        toast.success("Meal updated");
      } else {
        await api.post("/meals", payload);
        toast.success("Meal added");
      }
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const toggle = (arr, val) => arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="meal-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit meal plan" : "New meal plan"}</DialogTitle>
          <DialogDescription>Items are shown to parents instantly with allergen badges.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input required type="date" value={f.plan_date} data-testid="meal-date"
              onChange={(e) => setF({ ...f, plan_date: e.target.value })}
              className="h-11 px-3 rounded-xl border border-border" />
            <select value={f.meal_type} data-testid="meal-type"
              onChange={(e) => setF({ ...f, meal_type: e.target.value })}
              className="h-11 px-3 rounded-xl border border-border">
              {MEAL_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <textarea placeholder="Items, comma separated (e.g. Roti, Dal, Salad)"
            value={f.items} onChange={(e) => setF({ ...f, items: e.target.value })}
            className="w-full px-3 py-2 rounded-xl border border-border" rows={2}
            data-testid="meal-items" />
          <div>
            <div className="text-xs font-semibold text-ink-soft mb-1 uppercase tracking-wider">Age groups</div>
            <div className="flex flex-wrap gap-2">
              {["Infant", "Toddler", "Nursery", "KG1", "KG2"].map((a) => {
                const on = f.age_groups.includes(a);
                return (
                  <button type="button" key={a}
                    onClick={() => setF({ ...f, age_groups: toggle(f.age_groups, a) })}
                    data-testid={`meal-age-${a}`}
                    className={`px-3 py-1 rounded-full text-xs ${on ? "bg-terracotta text-white" : "bg-white border border-border"}`}>
                    {a}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-ink-soft mb-1 uppercase tracking-wider">Allergens</div>
            <div className="flex flex-wrap gap-2">
              {ALL_ALLERGENS.map((a) => {
                const on = f.allergens.includes(a);
                return (
                  <button type="button" key={a}
                    onClick={() => setF({ ...f, allergens: toggle(f.allergens, a) })}
                    data-testid={`meal-allergen-${a}`}
                    className={`px-3 py-1 rounded-full text-xs ${on ? "bg-mustard text-ink" : "bg-white border border-border"}`}>
                    {a}
                  </button>
                );
              })}
            </div>
          </div>
          <textarea placeholder="Notes (optional)" rows={2} value={f.notes}
            onChange={(e) => setF({ ...f, notes: e.target.value })}
            className="w-full px-3 py-2 rounded-xl border border-border" />
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-border">Cancel</button>
            <button disabled={busy} type="submit" data-testid="meal-submit"
              className="flex-1 h-11 rounded-full bg-terracotta text-white font-semibold disabled:opacity-60">
              {busy ? "Saving…" : (isEdit ? "Save changes" : "Add meal")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
